import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('INBOXAPP_CREATE');

export const inboxappCreateCommand: SelfContainedCommand = {
  id: 'inboxapp_create',
  description: '📬 CREATE: Create an Inbox app entity — opens the AI Inbox in a Space',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Display name (default: "Inbox")',
        required: false,
        defaultValue: 'Inbox',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID (uses current space if not provided)',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder ID (root level if not provided)',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: {},
        expectedOutput: createSuccessResult('Inbox created', { entityId: 'inbox_abc' }),
        description: 'Create an InboxApp entity for the current space',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { name = 'Inbox', spaceId: providedSpaceId, folderId } = params;
    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.info('[inboxapp_create] Creating InboxApp:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newEntity = await entityStore.actions.createEntity({
        entityType: 'InboxApp',
        name: name.trim() || 'Inbox',
        icon: '📬',
        spaceId,
        skeleton: {
          '@type': 'InboxApp',
          groupIds: folderId ? [folderId] : [],
        },
      } as any);

      const entityId = newEntity._id || newEntity.id;
      log.info('[inboxapp_create] ✅ InboxApp created:', entityId);

      // Add to dataspace tree
      if (entityId && spaceId) {
        try {
          const parentId = folderId || spaceId;
          const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
          useDataspaceTreeStore.getState()._applyAddNode(`${parentId}:${entityId}`, {
            id: entityId,
            parentId,
            pos: 'Z',
            expanded: false,
            kind: 'canonical',
          });
        } catch (treeError) {
          log.warn('[inboxapp_create] Tree update failed (non-fatal):', treeError);
        }
      }

      // Open window
      try {
        const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
        await useCommandRegistryStore.getState().executeCommand('space_open_window', { entityId });
      } catch (windowError) {
        log.warn('[inboxapp_create] Window open failed (non-fatal):', windowError);
      }

      return createSuccessResult(`Inbox created: ${name}`, { entityId, name: newEntity.name });
    } catch (error: any) {
      log.error('[inboxapp_create] Error:', error);
      return createErrorResult(
        'Failed to create Inbox',
        error.message || 'Unknown error',
        'execution'
      );
    }
  },
};

entityCreationRegistry.register({
  commandId: 'inboxapp_create',
  entityType: 'InboxApp',
  displayName: 'Inbox',
  icon: { type: 'phosphor', phosphorIcon: 'Tray', phosphorWeight: 'duotone' },
  description: 'AI Inbox — outputs, briefings, triage results',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 2,
});
